import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { es as esLocale, enUS as enLocale } from "date-fns/locale";
import { useLang } from "@/context/LangContext";
import { BOOKING } from "@/constants/testIds";
import { activities } from "@/data/activities";
import { Calendar as CalendarIcon, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  email: "",
  phone: "",
  activity: "",
  date: null,
  participants: 2,
  experience_level: "principiante",
  message: "",
};

export default function BookingForm({ defaultActivity }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState({ ...initial, activity: defaultActivity || "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.activity || !form.date || !form.name || !form.email) {
      toast.error(lang === "es" ? "Completa los campos obligatorios" : "Please fill required fields");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        activity: form.activity,
        date: format(form.date, "yyyy-MM-dd"),
        participants: Number(form.participants) || 1,
        experience_level: form.experience_level,
        message: form.message,
      };
      await axios.post(`${API}/bookings`, payload);
      setDone(true);
      toast.success(t("booking.success.title"));
      setForm({ ...initial });
    } catch (err) {
      console.error(err);
      toast.error(t("booking.error"));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={BOOKING.success}
        className="bg-white border-2 border-[#2D5A3C] p-10 rv-shadow-hard"
      >
        <div className="w-14 h-14 bg-[#2D5A3C] flex items-center justify-center mb-6">
          <Check className="w-7 h-7 text-white" strokeWidth={3} />
        </div>
        <h3 className="font-display font-black text-3xl text-[#1A1B1A]">
          {t("booking.success.title")}
        </h3>
        <p className="mt-3 text-[#5C635A] leading-relaxed">
          {t("booking.success.body")}
        </p>
        <a
          href="mailto:sustraiaventura@gmail.com"
          className="inline-flex items-center gap-2 mt-6 text-[#F26419] font-bold tracking-wider text-sm uppercase hover:text-[#D95512]"
        >
          <Mail className="w-4 h-4" />
          sustraiaventura@gmail.com
        </a>
        <button
          onClick={() => setDone(false)}
          className="block mt-8 text-xs tracking-[0.2em] uppercase font-bold text-[#1A1B1A] underline"
        >
          {lang === "es" ? "Nueva reserva" : "New booking"}
        </button>
      </div>
    );
  }

  const inputCls =
    "w-full bg-transparent border-0 border-b-2 border-[#D1D5DB] rounded-none px-0 py-3 text-base text-[#1A1B1A] placeholder:text-[#5C635A]/60 focus:outline-none focus:border-[#2D5A3C] transition-colors";
  const labelCls =
    "block text-xs tracking-[0.2em] uppercase font-bold text-[#5C635A] mb-2";

  return (
    <form
      onSubmit={submit}
      data-testid={BOOKING.form}
      className="bg-white border-2 border-[#1A1B1A] p-6 md:p-10 rv-shadow-hard"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className={labelCls}>{t("booking.label.name")} *</label>
          <input
            data-testid={BOOKING.inputName}
            type="text"
            required
            value={form.name}
            onChange={(e) => update("name", e.target.value)}
            className={inputCls}
            placeholder="Jon Aguirre"
          />
        </div>
        <div>
          <label className={labelCls}>{t("booking.label.email")} *</label>
          <input
            data-testid={BOOKING.inputEmail}
            type="email"
            required
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
            className={inputCls}
            placeholder="tu@email.com"
          />
        </div>
        <div>
          <label className={labelCls}>{t("booking.label.phone")}</label>
          <input
            data-testid={BOOKING.inputPhone}
            type="tel"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
            className={inputCls}
            placeholder="+34 ..."
          />
        </div>
        <div>
          <label className={labelCls}>{t("booking.label.participants")}</label>
          <input
            data-testid={BOOKING.inputParticipants}
            type="number"
            min={1}
            max={50}
            value={form.participants}
            onChange={(e) => update("participants", e.target.value)}
            className={inputCls}
          />
        </div>

        <div>
          <label className={labelCls}>{t("booking.label.activity")} *</label>
          <Select
            value={form.activity}
            onValueChange={(v) => update("activity", v)}
          >
            <SelectTrigger
              data-testid={BOOKING.selectActivity}
              className="w-full bg-transparent border-0 border-b-2 border-[#D1D5DB] rounded-none px-0 py-3 h-auto text-base focus:ring-0 focus:border-[#2D5A3C]"
            >
              <SelectValue placeholder={t("booking.placeholder.activity")} />
            </SelectTrigger>
            <SelectContent>
              {activities.map((a) => (
                <SelectItem key={a.slug} value={a.slug}>
                  {a.title[lang]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className={labelCls}>{t("booking.label.date")} *</label>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={BOOKING.inputDate}
                className="w-full flex items-center justify-between bg-transparent border-0 border-b-2 border-[#D1D5DB] rounded-none px-0 py-3 text-base text-left hover:border-[#2D5A3C] transition-colors"
              >
                <span className={form.date ? "text-[#1A1B1A]" : "text-[#5C635A]/60"}>
                  {form.date
                    ? format(form.date, "PPP", { locale: lang === "es" ? esLocale : enLocale })
                    : t("booking.placeholder.date")}
                </span>
                <CalendarIcon className="w-4 h-4 text-[#5C635A]" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={form.date}
                onSelect={(d) => update("date", d)}
                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                locale={lang === "es" ? esLocale : enLocale}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>{t("booking.label.level")}</label>
          <Select
            value={form.experience_level}
            onValueChange={(v) => update("experience_level", v)}
          >
            <SelectTrigger
              data-testid={BOOKING.selectLevel}
              className="w-full bg-transparent border-0 border-b-2 border-[#D1D5DB] rounded-none px-0 py-3 h-auto text-base focus:ring-0 focus:border-[#2D5A3C]"
            >
              <SelectValue placeholder={t("booking.placeholder.level")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="principiante">{t("booking.level.beginner")}</SelectItem>
              <SelectItem value="intermedio">{t("booking.level.intermediate")}</SelectItem>
              <SelectItem value="avanzado">{t("booking.level.advanced")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>{t("booking.label.message")}</label>
          <textarea
            data-testid={BOOKING.inputMessage}
            rows={3}
            value={form.message}
            onChange={(e) => update("message", e.target.value)}
            className={`${inputCls} resize-none`}
            placeholder={t("booking.placeholder.message")}
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid={BOOKING.submit}
        disabled={loading}
        className="mt-8 w-full md:w-auto bg-[#F26419] hover:bg-[#D95512] disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold tracking-[0.1em] uppercase px-10 py-5 text-sm transition-colors"
      >
        {loading ? t("booking.submitting") : t("booking.submit")}
      </button>
    </form>
  );
}
